import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ArrowRight, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

import {
    DashboardSection, DashboardTable, DistributionBars, SectionGrid, StatTile, TileGrid,
} from "../dashboard";
import { Button } from "../ui/button";
import StatusPill from "./StatusPill";

/* The RM's block on the Appraisal dashboard: how the direct reports' appraisals
   are spread, and which ones are waiting on this manager.

   Built from the shared dashboard kit. The status split used to be a chart.js
   doughnut; it is bars now because that is what the kit is for — a handful of
   labelled categories reads better as labelled rows than as a ring whose
   legend you have to match back to the slices. chart.js stays for the places
   where the shape of the data is the point, such as the payroll trend. */

const STATUS_BUCKETS = [
    { key: "pending_employee_submission", label: "Pending Submission", bar: "bg-amber-500" },
    { key: "in_progress", label: "In Progress", bar: "bg-blue-500" },
    { key: "submitted_to_rm", label: "Submitted to Me", bar: "bg-indigo-500" },
    { key: "sent_back_by_rm", label: "Sent Back", bar: "bg-orange-500" },
    { key: "pending_hr_approval", label: "With HR", bar: "bg-violet-500" },
    { key: "published", label: "Published", bar: "bg-emerald-500" },
    { key: "overdue", label: "Overdue", bar: "bg-red-500" },
];

const PENDING_COLUMNS = [
    {
        key: "employee",
        header: "Employee",
        render: (a) => (
            <span className="block min-w-0">
                <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {a.employee?.name || "Employee"}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                    {a.employee?.designation}
                </span>
            </span>
        ),
    },
    {
        key: "status",
        header: "Status",
        className: "text-right",
        headClassName: "text-right",
        render: (a) => <StatusPill value={a.status} />,
    },
];

const RMTeamSummary = ({ appraisals = [] }) => {
    const navigate = useNavigate();

    const counts = useMemo(() => {
        const c = Object.fromEntries(STATUS_BUCKETS.map((b) => [b.key, 0]));
        for (const a of appraisals) {
            if (c[a.status] !== undefined) c[a.status] += 1;
        }
        return c;
    }, [appraisals]);

    const totalTeam = appraisals.length;
    const pendingMine = counts.submitted_to_rm + counts.sent_back_by_rm;
    const inFlight = counts.pending_employee_submission + counts.in_progress;
    const completed = counts.published;

    // Zero buckets are dropped so the list stays as terse as the doughnut's
    // legend was.
    const statusRows = useMemo(
        () => STATUS_BUCKETS
            .filter((b) => counts[b.key] > 0)
            .map((b) => ({ key: b.key, label: b.label, count: counts[b.key], bar: b.bar })),
        [counts],
    );

    const completionPercent = totalTeam ? Math.round((completed * 100) / totalTeam) : null;

    const pendingReviewList = useMemo(
        () => appraisals.filter((a) => a.status === "submitted_to_rm").slice(0, 5),
        [appraisals],
    );

    return (
        <div className="space-y-4">
            <DashboardSection
                title="Team Performance Overview"
                action={
                    <Button size="sm" variant="brand" onClick={() => navigate("/appraisal/team-review")}>
                        Open Team Review <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                }
            >
                <TileGrid>
                    <StatTile Icon={Users} label="Team Size" value={totalTeam} />
                    <StatTile
                        Icon={AlertTriangle}
                        label="Pending Review"
                        value={pendingMine}
                        tone={pendingMine ? "warning" : "default"}
                        onClick={() => navigate("/appraisal/team-review")}
                    />
                    <StatTile Icon={Clock} label="In Flight" value={inFlight} tone="info" />
                    <StatTile
                        Icon={CheckCircle2}
                        label="Published"
                        value={completed}
                        tone="success"
                        hint={completionPercent === null ? undefined : `${completionPercent}% of the team`}
                    />
                </TileGrid>
            </DashboardSection>

            <SectionGrid>
                <DashboardSection title="Status Distribution">
                    <DistributionBars
                        rows={statusRows}
                        total={totalTeam}
                        colorFor={(row) => row.bar}
                        emptyText="No team appraisals yet."
                        labelWidth="w-40"
                    />
                </DashboardSection>

                <DashboardSection
                    title="Pending My Review"
                    action={
                        pendingReviewList.length > 0 && (
                            <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/team-review")}>
                                View all <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        )
                    }
                >
                    <DashboardTable
                        columns={PENDING_COLUMNS}
                        rows={pendingReviewList}
                        onRowClick={(a) => navigate(`/appraisal/team-review/${a.id}`)}
                        minWidth="min-w-[320px]"
                        empty="All caught up."
                    />
                </DashboardSection>
            </SectionGrid>
        </div>
    );
};

export default RMTeamSummary;
